/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, June 4th 2022, 4:00:05 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Stack from "@mui/material/Stack";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Grid from "@mui/material/Grid";
import GroupsIcon from "@mui/icons-material/Groups";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import IconButton from "@mui/material/IconButton";
import { useServiceBuilderContext } from "service-builder/providers";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { ActionDialog } from "ui-widgets";
import { COLORSCHEME } from "theme/styles";
import { useAPIListTeams } from "teams/services/TeamsAPI";
import { Team } from "lib/schemas";
import { EmptyServiceTeamsCTA } from "./EmptyServiceTeamsCTA";
import { ProcessDefinitionLinkTeamButton } from "./ProcessDefinitionLinkTeam";

export const ProcessDefinitionTeamsList = () => {
  const {
    processDefinitionContext: { currentProcessDefinition },
    patchCurrentProcessDefinition,
  } = useServiceBuilderContext();

  const {
    teams: processDefinitionTeams,
    isMakingAPIRequest,
    httpResponseStatusCode,
    isAPIError,
  } = useAPIListTeams({
    makeRequestOnInitialMount: true,
    apiQueryParams: { group: currentProcessDefinition.groups.length ? currentProcessDefinition.groups : [0] },
    dependencyList: [currentProcessDefinition.groups],
  });

  const removeTeam = (team: Team) => {
    if (!team) return;

    patchCurrentProcessDefinition({
      groups: currentProcessDefinition.groups.filter((group_id) => group_id !== team.group),
    });
  };

  return (
    <APIListProgressWrapper
      items={processDefinitionTeams}
      showLoader={isMakingAPIRequest}
      httpResponseStatusCode={httpResponseStatusCode}
      isAPIError={isAPIError}
      noDataMessage={<EmptyServiceTeamsCTA />}
    >
      <ProcessDefinitionLinkTeamButton />
      <Stack direction="column" spacing={2}>
        <List component="nav" aria-label="process-definition-teams-list">
          {processDefinitionTeams.map((team) => (
            <ListItemButton color={COLORSCHEME.secondaryVariant} key={`${team.id}`}>
              <Grid container>
                <Grid item md={2}>
                  <GroupsIcon color="primary" />
                </Grid>
                <Grid item md={5}>
                  <ListItemText primary={team.team_name} />
                </Grid>
                <Grid item md={3}>
                  <ListItemText primary={`${team.members.length} member(s)`} />
                </Grid>
                <Grid item md={1}>
                  <ActionDialog
                    title="Remove Team"
                    severity="warning"
                    dialogMessage={`Are you sure you want to remove the ${team.team_name} team from ${currentProcessDefinition.title}? Members of the ${team.team_name} team will no longer be able to access the service.`}
                    confirmationAction={() => removeTeam(team)}
                  >
                    {({ handleOpen }) => (
                      <IconButton edge="end" aria-label="remove" onClick={handleOpen} sx={{ height: 20, width: 20 }}>
                        <RemoveCircleOutlineIcon color="error" />
                      </IconButton>
                    )}
                  </ActionDialog>
                </Grid>
              </Grid>
            </ListItemButton>
          ))}
        </List>
      </Stack>
    </APIListProgressWrapper>
  );
};
